import LabelT from '@components/tailwind-components/Label/LabelT';
import { useOutsideClick } from '@hooks/useOutsideClick';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { IDropdownSearchInputProps } from '../../../interfaces/IInputProps';
import { GeneralTooltip } from '../../common/GeneralTooltip';
import MaterialIcon from '../../common/MaterialIcon';

export const DropDownSearchInput: React.FC<IDropdownSearchInputProps> = ({
  isEnabled = true,
  name,
  values,
  label,
  setValue,
  defaultInputValue,
  inputClassName = '',
  parentClassName = '',
  labelClassName = '',
  dropdownListClassName = '',
  dropdownItemClassName = '',
  placeholder,
  clearTrigger = 0,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>(defaultInputValue || '');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [selectedDisplayValue, setSelectedDisplayValue] = useState<string>(
    defaultInputValue || '',
  );
  const searchSuggestionRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (value: string) => {
    setSearchTerm(value || '');
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleOptionClick = (value: string, description: string) => {
    setValue(name, value);
    setSearchTerm(description || '');
    setSelectedOption(value);
    setSelectedDisplayValue(description || '');
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (selectedOption) {
      // Clear the input when focusing and there's a selected value
      setSearchTerm('');
    }
    setShowDropdown(true);
  };

  const handleInputBlur = (inputValue: string) => {
    // Check if user typed an exact match
    if (filteredOptions.length === 1) {
      const filteredOption = filteredOptions[0];
      if (
        filteredOption.description?.toLowerCase() === inputValue.toLowerCase()
      ) {
        return handleOptionClick(
          filteredOption.value,
          filteredOption.description,
        );
      }
    }

    // If no exact match and we have a selected value, restore it
    if (selectedOption && selectedDisplayValue) {
      setSearchTerm(selectedDisplayValue);
    } else if (inputValue.length) {
      resetSelection();
    }
  };

  const resetSelection = useCallback(() => {
    setShowDropdown(false);
    setSearchTerm('');
    setSelectedDisplayValue('');
    setValue(name, '');
    setSelectedOption('');
  }, [name, setValue]);

  const filteredOptions = values.filter((option) =>
    typeof searchTerm === 'string'
      ? option.description.toLowerCase().includes(searchTerm.toLowerCase())
      : values,
  );

  useEffect(() => {
    if (clearTrigger > 0) {
      resetSelection();
    }
  }, [clearTrigger, resetSelection]);

  useEffect(() => {
    setSearchTerm(defaultInputValue || '');
    setSelectedDisplayValue(defaultInputValue || '');

    // Find the selected option value based on display value
    const selectedFont = values.find(
      (font) => font.description === defaultInputValue,
    );
    if (selectedFont) {
      setSelectedOption(selectedFont.value);
    }
  }, [defaultInputValue, values]);

  useOutsideClick(searchSuggestionRef, () => {
    setShowDropdown(false);
    // Restore the selected value when clicking outside
    if (selectedOption && selectedDisplayValue) {
      setSearchTerm(selectedDisplayValue);
    }
  });

  return (
    <div className={`w-full ${parentClassName}`}>
      <LabelT className={`!px-1 text-xs ${labelClassName}`} text={label} />
      <div
        className={`relative ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}
        ref={searchSuggestionRef}>
        <input
          type='text'
          placeholder={placeholder}
          disabled={!isEnabled}
          value={searchTerm || ''}
          onFocus={handleInputFocus}
          onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
            handleInputBlur(event.target.value);
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange(event.target.value)
          }
          className={`block w-full py-1.5 pl-2 pr-9 text-xs leading-5 outline-none font-normal ring-1 ring-inset ring-gray-200 focus:ring-1 focus:ring-inset focus:ring-primary-base rounded-md ${inputClassName} ${!isEnabled ? 'bg-gray-200 cursor-default' : 'bg-white'}`}
          autoComplete='off'
        />

        {isEnabled && (
          <div
            onClick={() => {
              resetSelection();
            }}
            className='absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer z-10'>
            <GeneralTooltip
              text='Clear input'
              placement='left'
              className='!right-7'>
              <MaterialIcon
                icon={'close'}
                classes={['text-gray-600', 'flex', 'text-base']}
              />
            </GeneralTooltip>
          </div>
        )}

        <div
          className={`bg-white rounded-md shadow-lg absolute max-h-[200px] top-[31px] z-50 w-[calc(100%-8px)] overflow-y-auto scrollbar-flow text-gray-600 ring-1 ring-inset ring-gray-200 ${dropdownListClassName} ${
            showDropdown ? 'block' : 'hidden'
          }`}>
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              onMouseDown={(event) => {
                // Prevent the mousedown event from causing the input to blur
                event.preventDefault();
                handleOptionClick(option.value, option.description);
              }}
              style={{ fontFamily: option.fontFamily || undefined }}
              className={`border-b font-light text-xs leading-5 py-1.5 px-2 cursor-pointer hover:bg-primary-base hover:text-white word-break-style ${
                selectedOption === option.value
                  ? '!bg-primary-base text-white'
                  : ''
              } ${dropdownItemClassName}`}>
              {option.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DropDownSearchInput;
